/**
 * Boxes (D22): endereço físico das sobras. Cadastro simples: código,
 * nome, depósito, ordem, observação. Desativar não apaga — sobra que está
 * lá continua apontando para o Box.
 *
 * Edição completa (pedido do dono, 11/09): o mesmo painel cria ou edita;
 * o backend valida código único na unidade e depósito da mesma unidade.
 */
import React, { useEffect, useState } from "react";
import { FiAlertCircle, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import Checkbox from "../../../components/Checkbox";
import ConfirmDialog from "../../../components/ConfirmDialog";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { listarDepositos } from "../../../services/estoque";
import { apagarBox, atualizarBox, criarBox, desativarBox, linhasDe } from "../../../services/suprimentos";
import {
  Card, ErrorMsg, Field, Form, FormFooter, FormGrid, IconButton, Input, Pill,
  PrimaryButton, SecondaryButton, Select, Table, TableWrapper, Textarea, TruncCell,
} from "../styles";
import { Vazio } from "../_shared/Estados";

const VAZIO = { codigo: "", nome: "", deposito: "", ordem: "100", observacao: "", ativo: true };

function formDe(box) {
  if (!box) return { ...VAZIO };
  return {
    id: box.id,
    codigo: box.codigo || "",
    nome: box.nome || "",
    deposito: box.deposito || "",
    ordem: String(box.ordem ?? 100),
    observacao: box.observacao || "",
    ativo: box.ativo !== false,
  };
}

export default function BoxesTab({ boxes, onMudou }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();
  // Bloco 6 (11/09/2026): box tem permissoes proprias.
  const podeCriar = isMaster || hasAllPermissions(["box:create"]);
  const podeEditar = isMaster || hasAllPermissions(["box:edit"]);
  const podeApagar = isMaster || hasAllPermissions(["box:delete"]);
  const [apagando, setApagando] = useState(null);
  const [emUso, setEmUso] = useState(null);
  const [depositos, setDepositos] = useState([]);
  const [form, setForm] = useState(null);
  const [erro, setErro] = useState(null);
  const editando = Boolean(form?.id);

  useEffect(() => {
    listarDepositos(request).then((p) => setDepositos(linhasDe(p))).catch(() => setDepositos([]));
  }, [request]);

  const set = (campo) => (e) => setForm((p) => ({ ...p, [campo]: e.target.value }));

  const salvar = async (e) => {
    e.preventDefault();
    if (!form.codigo.trim()) { setErro("Código é obrigatório."); return; }
    setErro(null);
    const dados = {
      codigo: form.codigo.trim().toUpperCase(),
      nome: form.nome,
      deposito: form.deposito || null,
      ordem: Number(form.ordem) || 100,
      observacao: form.observacao,
    };
    try {
      if (editando) {
        await atualizarBox(request, form.id, { ...dados, ativo: form.ativo });
        toast.success(`Box ${dados.codigo} atualizado.`);
      } else {
        await criarBox(request, dados);
        toast.success(`Box ${dados.codigo} criado.`);
      }
      setForm(null);
      await onMudou();
    } catch (err) {
      const p = err?.payload;
      setErro(p?.detalhe || p?.codigo?.[0] || p?.deposito?.[0] || err?.message || "Erro ao salvar box.");
    }
  };

  const alternar = async (b) => {
    if (b.ativo) await desativarBox(request, b.id);
    else await atualizarBox(request, b.id, { ativo: true });
    await onMudou();
  };

  // Apagar de verdade; com sobra/inventario apontando o backend devolve 409
  // e a tela oferece desativar.
  const confirmarApagar = async () => {
    const b = apagando;
    setApagando(null);
    try {
      await apagarBox(request, b.id);
      toast.success(`Box ${b.codigo} apagado.`);
      await onMudou();
    } catch (err) {
      if (err?.status === 409 || err?.payload?.em_uso) {
        setEmUso({ ...b, motivo: err?.payload?.detalhe });
        return;
      }
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao apagar box.");
    }
  };

  return (
    <Card>
      {podeCriar && (
        <div style={{ marginBottom: 10 }}>
          <SecondaryButton type="button" onClick={() => setForm(formDe(null))}>
            <FiPlus size={12} /> Novo box
          </SecondaryButton>
        </div>
      )}
      {boxes.length === 0 ? (
        <Vazio
          titulo="Nenhum box cadastrado"
          descricao="Cadastre os boxes numerados do galpão de sobras — é onde cada retalho fica guardado."
          acoes={podeCriar ? [{ label: "Novo box", onClick: () => setForm(formDe(null)) }] : []}
        />
      ) : (
        <TableWrapper>
          <Table>
            <colgroup>
              <col style={{ width: "12%" }} /><col style={{ width: "26%" }} /><col style={{ width: "20%" }} />
              <col style={{ width: "8%" }} /><col style={{ width: "10%" }} /><col style={{ width: "10%" }} /><col style={{ width: "14%" }} />
            </colgroup>
            <thead>
              <tr><th>Código</th><th>Nome</th><th>Depósito</th><th>Ordem</th><th>Disponíveis</th><th>Situação</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {boxes.map((b) => (
                <tr key={b.id}>
                  <td><TruncCell>{b.codigo}</TruncCell></td>
                  <td title={b.nome}><TruncCell>{b.nome || "—"}</TruncCell></td>
                  <td title={b.deposito_nome}><TruncCell>{b.deposito_nome || "—"}</TruncCell></td>
                  <td>{b.ordem}</td>
                  <td>{b.sobras_disponiveis}</td>
                  <td><Pill $tone={b.ativo ? "success" : "neutral"}>{b.ativo ? "Ativo" : "Inativo"}</Pill></td>
                  <td>
                    {(podeEditar || podeApagar) && (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        {podeEditar && (
                          <IconButton type="button" onClick={() => setForm(formDe(b))} title="Editar box" aria-label={`Editar ${b.codigo}`}>
                            <FiEdit2 size={12} />
                          </IconButton>
                        )}
                        {podeEditar && (
                          <SecondaryButton type="button" onClick={() => alternar(b)}>
                            {b.ativo ? "Desativar" : "Reativar"}
                          </SecondaryButton>
                        )}
                        {podeApagar && (
                          <IconButton type="button" onClick={() => setApagando(b)} title="Apagar box" aria-label={`Apagar ${b.codigo}`}>
                            <FiTrash2 size={12} />
                          </IconButton>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}

      <SlideOverPanel open={Boolean(form)} onClose={() => setForm(null)} title={editando ? `Editar box ${form.codigo}` : "Novo box"}>
        {form && (
          <Form onSubmit={salvar}>
            <FormGrid>
              <Field>
                Código *
                <Input value={form.codigo} onChange={set("codigo")} placeholder="B01" maxLength={20} autoFocus={!editando} />
              </Field>
              <Field>
                Ordem
                <Input type="number" value={form.ordem} onChange={set("ordem")} />
              </Field>
            </FormGrid>
            <Field>
              Nome
              <Input value={form.nome} onChange={set("nome")} placeholder="Prateleira 1, lado esquerdo" maxLength={80} />
            </Field>
            <Field>
              Depósito
              <Select value={form.deposito} onChange={set("deposito")}>
                <option value="">— Depósito de sobras da configuração —</option>
                {depositos.filter((d) => d.ativo || d.id === form.deposito).map((d) => <option key={d.id} value={d.id}>{d.nome}</option>)}
              </Select>
            </Field>
            <Field>
              Observação
              <Textarea rows={2} maxLength={200} value={form.observacao} onChange={set("observacao")} />
            </Field>
            {editando && (
              <Field>
                <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                  <Checkbox checked={form.ativo} onChange={(e) => setForm((p) => ({ ...p, ativo: e.target.checked }))} />
                  Box ativo (aparece para guardar sobras)
                </label>
              </Field>
            )}
            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
            <FormFooter>
              <SecondaryButton type="button" onClick={() => setForm(null)}>Cancelar</SecondaryButton>
              <PrimaryButton type="submit">{editando ? "Salvar alterações" : "Criar box"}</PrimaryButton>
            </FormFooter>
          </Form>
        )}
      </SlideOverPanel>

      <ConfirmDialog
        open={Boolean(apagando)}
        titulo={`Apagar o box ${apagando?.codigo}?`}
        descricao="Só apaga se nenhuma sobra nem inventário apontar para ele. Nesse caso você poderá desativar em vez de apagar."
        confirmarLabel="Sim, apagar"
        cancelarLabel="Não"
        onConfirmar={confirmarApagar}
        onCancelar={() => setApagando(null)}
      />
      <ConfirmDialog
        open={Boolean(emUso)}
        titulo={`${emUso?.codigo} está em uso e não pode ser apagado`}
        descricao={`${emUso?.motivo || "Há sobras ou inventários apontando para este box."} Deseja desativá-lo? Ele some das listas e as sobras continuam onde estão.`}
        confirmarLabel="Sim, desativar"
        cancelarLabel="Não"
        tone="warning"
        onConfirmar={async () => { const b = emUso; setEmUso(null); await desativarBox(request, b.id); await onMudou(); }}
        onCancelar={() => setEmUso(null)}
      />
    </Card>
  );
}
